import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import ChartCard from './ChartCard';
import ChartLegend from './ChartLegend';
import { RechartsTooltip } from './ChartTooltip';
import {
  BAR_MAX_SIZE,
  CHART_INK,
  CHART_SURFACE,
  PLAYER_COLORS,
} from './chartTheme';
import { formatBucketLabel, formatBucketTitle } from './formatters';
import { getWeekendLegendItem, renderWeekendLines } from './weekendLines';
import {
  PLAYER_CATEGORIES,
  PlayerCounts,
  TimeBucket,
} from '../../utils/statistics';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';

interface PlayersChartProps {
  title: string;
  subtitle: string;
  buckets: TimeBucket[];
  /** Unique players in the whole period, per category. */
  totals: PlayerCounts;
}

const PlayersChart = ({
  title,
  subtitle,
  buckets,
  totals,
}: PlayersChartProps) => {
  const { t, i18n } = useTranslation();

  const data = useMemo(
    () =>
      buckets.map((b) => ({
        ...b,
        label: formatBucketLabel(dayjs(b.date), b.unit, buckets.length, i18n.language),
      })),
    [buckets, i18n.language]
  );

  const labels = useMemo(
    () => ({
      regular: t(COMMON_TRANSLATE_KEYS.STATS_PLAYERS_REGULAR),
      new: t(COMMON_TRANSLATE_KEYS.STATS_PLAYERS_NEW),
      unregistered: t(COMMON_TRANSLATE_KEYS.STATS_PLAYERS_UNREGISTERED),
    }),
    [t]
  );

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      legend={
        <ChartLegend
          items={[
            ...PLAYER_CATEGORIES.map((key) => ({
              key,
              label: labels[key],
              color: PLAYER_COLORS[key],
              value: String(totals[key]),
            })),
            ...getWeekendLegendItem(
              buckets,
              t(COMMON_TRANSLATE_KEYS.STATS_WEEKEND)
            ),
          ]}
        />
      }
    >
      <div className="h-[240px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
            <XAxis
              dataKey="label"
              tick={{ fill: CHART_INK.muted, fontSize: 12 }}
              axisLine={{ stroke: CHART_INK.axis }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={8}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fill: CHART_INK.muted, fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ fill: 'rgba(255,255,255,0.05)' }}
              content={
                <RechartsTooltip
                  labels={labels}
                  getTitle={(item) =>
                    formatBucketTitle(dayjs(item.date), item.unit, i18n.language)
                  }
                />
              }
            />
            {PLAYER_CATEGORIES.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                stackId="players"
                fill={PLAYER_COLORS[key]}
                stroke={CHART_SURFACE}
                strokeWidth={1}
                maxBarSize={BAR_MAX_SIZE}
                radius={index === PLAYER_CATEGORIES.length - 1 ? [4, 4, 0, 0] : 0}
                isAnimationActive={false}
              />
            ))}
            {renderWeekendLines(data)}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
};

export default PlayersChart;
